/**
 * Ground plane for shadows where the scan has no surface (a scanned object with no floor, a
 * model hanging over empty space).
 *
 * The scan receives shadows itself (splatShadowShader.ts), so this plane must not darken it a
 * second time. It draws after the splats (in TranslucentEntityLayer's transparent sub-layer)
 * with the engine's shadow-catcher material (multiplicative: 1 where lit, the shadow where not)
 * and fades itself out wherever the splat depth map (SplatDepthPass) has a splat surface at or
 * in front of the plane. While the depth map is not valid it shows nothing.
 *
 * Before the overhaul this plane drew before the splats, so on a scan with a floor the floor's
 * splats painted over it and no shadow was visible.
 */
import * as pc from 'playcanvas';
import { SPLAT_DEPTH_MAP_UNIFORM, SPLAT_DEPTH_PARAMS_UNIFORM } from '../../utils/materialOpacity';

const DECLARATION_GLSL = /* glsl */ `
uniform vec4 ${SPLAT_DEPTH_PARAMS_UNIFORM};
uniform highp sampler2D ${SPLAT_DEPTH_MAP_UNIFORM};
#ifndef VIEWPORT_SIZE
    #define VIEWPORT_SIZE
    uniform vec4 viewport_size;
#endif
#ifndef CAMERAPLANES
    #define CAMERAPLANES
    uniform vec4 camera_params;
#endif
float groundSplatDepthAt(vec2 uv) {
    ivec2 size = textureSize(${SPLAT_DEPTH_MAP_UNIFORM}, 0);
    ivec2 texel = clamp(ivec2(uv * vec2(size)), ivec2(0), size - 1);
    vec4 data = texelFetch(${SPLAT_DEPTH_MAP_UNIFORM}, texel, 0);
    #ifdef CAPS_TEXTURE_FLOAT_RENDERABLE
        return data.r;
    #else
        uint bits = (uint(data.r * 255.0) << 24u) | (uint(data.g * 255.0) << 16u) | (uint(data.b * 255.0) << 8u) | uint(data.a * 255.0);
        return uintBitsToFloat(bits);
    #endif
}
float groundPlaneVisibility() {
    if (${SPLAT_DEPTH_PARAMS_UNIFORM}.x < 0.5) return 0.0;
    float fragDepth = camera_params.w == 0.0 ? 1.0 / gl_FragCoord.w : camera_params.z + gl_FragCoord.z * (camera_params.y - camera_params.z);
    float splatDepth = groundSplatDepthAt(gl_FragCoord.xy * viewport_size.zw);
    // A splat surface at or in front of the plane receives its own shadow.
    float tolerance = max(0.15, fragDepth * 0.03);
    return clamp((splatDepth - fragDepth - tolerance) / tolerance, 0.0, 1.0);
}
`;

const MAIN_END_GLSL = /* glsl */ `
    gl_FragColor.rgb = mix(vec3(1.0), gl_FragColor.rgb, groundPlaneVisibility());
`;

const DECLARATION_WGSL = /* wgsl */ `
uniform ${SPLAT_DEPTH_PARAMS_UNIFORM}: vec4f;
var ${SPLAT_DEPTH_MAP_UNIFORM}: texture_2d<uff>;
#ifndef VIEWPORT_SIZE
    #define VIEWPORT_SIZE
    uniform viewport_size: vec4f;
#endif
#ifndef CAMERAPLANES
    #define CAMERAPLANES
    uniform camera_params: vec4f;
#endif
fn groundSplatDepthAt(uv: vec2f) -> f32 {
    let size = vec2i(textureDimensions(${SPLAT_DEPTH_MAP_UNIFORM}, 0));
    let texel = clamp(vec2i(uv * vec2f(size)), vec2i(0), size - vec2i(1));
    return textureLoad(${SPLAT_DEPTH_MAP_UNIFORM}, texel, 0).r;
}
fn groundPlaneVisibility() -> f32 {
    if (uniform.${SPLAT_DEPTH_PARAMS_UNIFORM}.x < 0.5) { return 0.0; }
    var fragDepth: f32;
    if (uniform.camera_params.w == 0.0) {
        fragDepth = 1.0 / pcPosition.w;
    } else {
        fragDepth = uniform.camera_params.z + pcPosition.z * (uniform.camera_params.y - uniform.camera_params.z);
    }
    let splatDepth = groundSplatDepthAt(pcPosition.xy * uniform.viewport_size.zw);
    let tolerance = max(0.15, fragDepth * 0.03);
    return clamp((splatDepth - fragDepth - tolerance) / tolerance, 0.0, 1.0);
}
`;

const MAIN_END_WGSL = /* wgsl */ `
    output.color = vec4f(mix(vec3f(1.0), output.color.rgb, groundPlaneVisibility()), output.color.a);
`;

export interface ShadowGroundPlane {
    entity: pc.Entity;
    /** Move / resize; returns true when something changed. */
    set(y: number, scale: number): boolean;
    destroy(): void;
}

export function createShadowGroundPlane(app: pc.AppBase, layer: pc.Layer): ShadowGroundPlane {
    const material = new pc.StandardMaterial();
    material.name = 'ShadowGroundPlane';
    material.shadowCatcher = true;
    material.blendType = pc.BLEND_MULTIPLICATIVE;
    material.depthWrite = false;
    material.useSkybox = false;
    material.diffuse.set(0, 0, 0);
    material.specular.set(0, 0, 0);
    const glsl = material.getShaderChunks(pc.SHADERLANGUAGE_GLSL);
    glsl.set('litUserDeclarationPS', DECLARATION_GLSL);
    glsl.set('litUserMainEndPS', MAIN_END_GLSL);
    const wgsl = material.getShaderChunks(pc.SHADERLANGUAGE_WGSL);
    wgsl.set('litUserDeclarationPS', DECLARATION_WGSL);
    wgsl.set('litUserMainEndPS', MAIN_END_WGSL);
    material.update();

    const entity = new pc.Entity('ShadowGroundPlane');
    entity.addComponent('render', {
        type: 'plane',
        material,
        castShadows: false,
        receiveShadows: true,
        layers: [layer.id],
    });
    let lastY = NaN;
    let lastScale = NaN;
    return {
        entity,
        set(y: number, scale: number): boolean {
            if (y === lastY && scale === lastScale) return false;
            lastY = y;
            lastScale = scale;
            entity.setLocalPosition(0, y, 0);
            entity.setLocalScale(scale, 1, scale);
            return true;
        },
        destroy(): void {
            entity.destroy();
            material.destroy();
        },
    };
}
